import styled from '@emotion/styled';
import { getLimitsByFeatureGroup } from '@hotjar/feature-flags';
import { useCurrentSite } from '@hotjar/site';
import { Alert, tokens } from '@hotjar/hotsauce';
import { upsell } from '@hotjar/webapp/domains/revenue/upsell';
import { noop } from '@hotjar/webapp/lib/function';
import { useTranslation } from 'react-i18next';
import type { Site } from '@hotjar/site';
import { useToolsCountQuery } from '../../../domain/common/query';

type FeatureGroup = 'surveys' | 'feedback';

const Container = styled.div`
  margin: 0 ${tokens.spacer.x5} ${tokens.spacer.x2} ${tokens.spacer.x5};
`;

export interface ShowWidgetLimitAlertProps {
  featureGroup: FeatureGroup;
  isEnabled: boolean | undefined;
}

export interface WidgetLimitAlertProps {
  featureGroup: FeatureGroup;
  limit: number;
  site: Site;
}

export const useShowWidgetLimitAlert = ({
  featureGroup,
  isEnabled,
}: ShowWidgetLimitAlertProps):
  | { visibility: 'loading' }
  | { visibility: 'hidden' }
  | { visibility: 'visible'; props: WidgetLimitAlertProps } => {
  const currentSite = useCurrentSite().data;
  const { data: widgetCount, isLoading: isWidgetCountLoading } = useToolsCountQuery(
    currentSite,
    featureGroup,
  );

  if (currentSite === undefined || isEnabled === undefined || isWidgetCountLoading)
    return { visibility: 'loading' };

  if (isEnabled === false || !currentSite.effectiveAskPlan || widgetCount === undefined)
    return { visibility: 'hidden' };

  const limit = getLimitsByFeatureGroup(featureGroup, currentSite.effectiveAskPlan);

  if (limit === undefined || limit > widgetCount) return { visibility: 'hidden' };

  return { visibility: 'visible', props: { limit, site: currentSite, featureGroup } };
};

export const WidgetLimitAlert = ({
  featureGroup,
  limit,
  site,
}: WidgetLimitAlertProps): JSX.Element => {
  const { t } = useTranslation('feedback');
  return (
    <Container>
      <Alert
        title={t('limits.reached.widgets.title', { featureGroup })}
        kind="warning"
        actionText={t('limits.reached.widgets.action')}
        onAction={() =>
          upsell({
            site,
            referrer:
              featureGroup === 'feedback' ? 'feedback.limit.reached' : 'polls.limit.reached',
          }).catch(noop)
        }
      >
        {t('limits.reached.widgets.message', { limit, featureGroup })}
      </Alert>
    </Container>
  );
};

export const StatefulWidgetLimitAlert = (
  props: Omit<ShowWidgetLimitAlertProps, 'isEnabled'>,
): JSX.Element | null => {
  const state = useShowWidgetLimitAlert({ ...props, isEnabled: true });
  if (state.visibility !== 'visible') return null;
  return <WidgetLimitAlert {...state.props} />;
};
